/**
 * Reusable "Save as PDF" / print helper.
 *
 * Prints ONLY the element with the given id as a clean, standalone document —
 * hiding the whole app shell (sidebar, top nav, notifications, floating widgets
 * like the feedback thumb) — then restores the page afterwards.
 */

const PRINT_STYLE_ID = "sb-print-document-style";

// Escape a string so it is safe inside a CSS content: "…" declaration.
const toCssString = (value) =>
  `"${String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;

const buildPrintCss = (rootId, opts) => {
  const {
    footerLeft = "",
    showPageNumbers = true,
    pageSize = "A4",
    margin = "20mm 15mm",
    footerColor = "#8790A4",
  } = opts;

  const root = `#${rootId}`;
  const bottomLeft = footerLeft
    ? `@bottom-left { content: ${toCssString(footerLeft)}; font-size: 8pt; color: ${footerColor}; }`
    : "";
  const bottomRight = showPageNumbers
    ? `@bottom-right { content: "Page " counter(page) " of " counter(pages); font-size: 8pt; color: ${footerColor}; }`
    : "";

  return `
    @media print {
      /* Hide everything, then reveal only the printable document */
      body * { visibility: hidden !important; }
      ${root}, ${root} * { visibility: visible !important; }
      ${root} {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        margin: 0;
        padding: 0;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      /* Drop anything explicitly marked as non-printable */
      .no-print { display: none !important; }

      /* Strip card chrome so it reads as a clean document */
      ${root} .ant-card,
      ${root} .ant-card-body {
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
        background: transparent !important;
      }

      /* Justify body text + avoid awkward page breaks */
      ${root} p,
      ${root} li { text-align: justify; }
      ${root} h1,
      ${root} h2,
      ${root} h3 { page-break-after: avoid; }
      /* Let long tables flow across pages (no big blank gaps), but keep each row
         intact and repeat the header row on every page. */
      ${root} tr { page-break-inside: avoid; }
      ${root} thead { display: table-header-group; }

      @page {
        size: ${pageSize};
        margin: ${margin};
        ${bottomLeft}
        ${bottomRight}
      }
    }
  `;
};

const removePrintStyle = () => {
  const existing = document.getElementById(PRINT_STYLE_ID);
  if (existing) existing.remove();
};

/**
 * Print the element with id `rootId` as a clean PDF-ready document.
 * @param {string} rootId - id of the container to print (e.g. "legal-print-root").
 * @param {object} [options]
 * @param {string} [options.footerLeft] - left footer text (e.g. confidentiality line).
 * @param {boolean} [options.showPageNumbers=true] - render "Page X of Y" in the footer.
 * @param {string} [options.pageSize="A4"] - @page size.
 * @param {string} [options.margin="20mm 15mm"] - @page margin.
 * @param {string} [options.footerColor="#8790A4"] - footer text color.
 */
export const printDocument = (rootId, options = {}) => {
  if (typeof window === "undefined" || !rootId) return;

  if (!document.getElementById(rootId)) {
    // eslint-disable-next-line no-console
    console.warn(`printDocument: no element found with id "${rootId}"`);
    return;
  }

  removePrintStyle(); // avoid stacking styles across repeated prints

  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.media = "print";
  style.textContent = buildPrintCss(rootId, options);
  document.head.appendChild(style);

  // Clean up after the print dialog closes (fires on both print and cancel).
  const cleanup = () => {
    removePrintStyle();
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);
  // Safety net for browsers that don't fire afterprint reliably.
  window.setTimeout(removePrintStyle, 60000);

  window.print();
};

export default printDocument;
